import i18next from 'i18next';
import React, {type ReactNode} from 'react';
import styled from 'styled-components';

import {Button} from '@famly/mf_input_button';
import {Avatar} from '@famly/mf_data-display_avatar';
import {Text} from '@famly/mf_data-display_text';
import {Stack} from '@famly/mf_layout_stack';
import {createBox, Box} from '@famly/mf_layout_box';
import {TARGET_IDS} from 'web-app/services/intercom-constants';
import {startIntercomSurvey} from 'web-app/util/intercom/start-intercom-survey';
import {checkIfFlagIsToggled, DebugLocalStorageFlags} from 'web-app/react/routes/debugger/local-storage-config';

import * as Tracking from '../tracking';
import type {Child} from '../types';
import {Overview} from './overview/overview';

// We experience issues with the modal height.
// If we don't subtract the modal header height,
// The columns of the grid container are not taking up the entire screen
// To solve this, we couple the constants with the actual height, padding and border-width values of the components.
// In this way, even if we change those values, the layout will still work as intended.

// All the calculations are needed to make the CTA buttons always stay on the botton of the screen
// As well as to create scrollable container in the remaining space of the right zone

const MODAL_TOP_BAR_HEIGHT = 80;

// Value of padding of the right zone which contains CTA buttons
const CONTAINER_PADDING = 10;

// Grid component has border top which we also need to substract
const BORDER_WIDTH = 1;

// In Modern Famly the value 1 equals 4px when it comes to paddings and margins
const DEFAULT_PADDING_VALUE = 4;

// We multiple by 2 at the end so we get both top and bottom padding value in px
const TOP_AND_BOTTOM_PADDING = CONTAINER_PADDING * DEFAULT_PADDING_VALUE * 2;

const Grid = createBox({
    display: 'grid',
    gridTemplateColumns: '60% 1fr',
    width: '100%',
    height: '100%',
});

const Form = styled.form`
    height: calc(100% - ${MODAL_TOP_BAR_HEIGHT + BORDER_WIDTH}px);
`;

type Props = {
    form: ReactNode;
    onClose: VoidFunction;
    saveButton: {
        onSave: VoidFunction;
        isDisabled: boolean;
        isLoading: boolean;
    };
    onCancel: VoidFunction;
    header: {
        copy: string;
        selectedChild: Child;
    };
    feedbackSurvey?: {
        surveyId: number;
        surveyTitle: string;
    };
    summary: {
        summaryContent?: ReactNode;
        summaryTitle: string;
    };
    planOverview: ReactNode;
    templateCheckbox?: ReactNode;
    deleteButton?: ReactNode;
    childrenErrors: ReactNode;
};

export const PlanModal = ({
    form,
    onClose,
    saveButton,
    onCancel,
    header,
    feedbackSurvey,
    summary,
    planOverview,
    templateCheckbox,
    deleteButton,
    childrenErrors,
}: Props) => {
    const onSubmit = event => {
        event.preventDefault();
        Tracking.editPlanSaved();
        saveButton.onSave();
    };

    const isPlanOverviewEnabled = checkIfFlagIsToggled(DebugLocalStorageFlags.PLAN_OVERVIEW_IN_CHILL_BILL.flag);

    return (
        <>
            <Stack
                justifyContent="space-between"
                px={6}
                alignItems="center"
                data-e2e-id="simplified-billing-modal"
                height={MODAL_TOP_BAR_HEIGHT}
            >
                <Stack alignItems="center" spacing={4}>
                    <Text variant="h6" emphasized data-intercom-target={TARGET_IDS.NEW_BILLING_PLAN_HEADER}>
                        {header.copy}
                    </Text>
                    <Stack alignItems="center" spacing={1}>
                        <Avatar src={header.selectedChild.profileImageUrl} size={28} />
                        <Text variant="body-small" color="n300">
                            {header.selectedChild.fullName}
                        </Text>
                    </Stack>
                </Stack>
                <Stack spacing={4}>
                    {feedbackSurvey ? (
                        <Button
                            onClick={() => startIntercomSurvey(feedbackSurvey.surveyId)}
                            variant="tertiary"
                            text={feedbackSurvey.surveyTitle}
                        />
                    ) : null}
                    <Button
                        onClick={onClose}
                        variant="transparent"
                        aria-label={i18next.t(
                            'react.simplifiedUsBillingPlan.translations.json.effectiveChangeDateBanner.close',
                        )}
                        size="regular"
                        icon="close"
                        data-e2e-id="close-button"
                    />
                </Stack>
            </Stack>
            <Form onSubmit={onSubmit}>
                <Grid borderTop="n100" borderTopWidth={BORDER_WIDTH}>
                    {form}
                    <Stack
                        p={CONTAINER_PADDING}
                        backgroundColor="n50"
                        borderLeft="n100"
                        direction="column"
                        justifyContent="space-between"
                        height={`calc(100vh - ${MODAL_TOP_BAR_HEIGHT + BORDER_WIDTH + TOP_AND_BOTTOM_PADDING}px)`}
                    >
                        <Stack direction="column" overflow="auto">
                            <Overview isPlanOverviewEnabled={isPlanOverviewEnabled}>
                                {selectedTab =>
                                    selectedTab === 'invoice-summary' ? summary.summaryContent : planOverview
                                }
                            </Overview>
                        </Stack>
                        <Box>
                            <Stack direction="column" spacing={4} alignSelf="flex-end">
                                {childrenErrors}
                            </Stack>
                            <Stack direction="column" mt={6} alignItems="flex-end" spacing={4}>
                                {templateCheckbox}
                                <Stack spacing={2} width="100%">
                                    {deleteButton}
                                    <Button
                                        variant="tertiary"
                                        text={i18next.t('react.simplifiedUsBillingPlan.translations.json.cancel')}
                                        onClick={onCancel}
                                        fullWidth
                                    />
                                    <Button
                                        isLoading={saveButton.isLoading}
                                        isDisabled={saveButton.isDisabled}
                                        type="submit"
                                        variant="primary"
                                        text={i18next.t('react.simplifiedUsBillingPlan.translations.json.save')}
                                        data-e2e-id="save-button"
                                        fullWidth
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>
                </Grid>
            </Form>
        </>
    );
};
